import { Color } from 'aos-ui-common/src/styles/Color'
import React, { FCWithChildren, PropsWithChildren, ReactNode } from 'react'

import { Box, MarginBoxProps, NumberValues } from '../../base/Box'
import { SeleniumProps } from '../../base/SeleniumProps'
import { Text, TextSize } from '../../base/Text'
import { ThemeVariant } from '../../base/ThemeVariant'

interface LabeledTextElementProps extends SeleniumProps, MarginBoxProps {
    label: ReactNode
    variant?: ThemeVariant
    size?: LabeledTextElementSize
    reversed?: boolean
    spacing?: NumberValues
}

export enum LabeledTextElementSize {
    Small,
    Large,
}

const fontSizes: Record<LabeledTextElementSize, { top: TextSize; bottom: TextSize }> = {
    [LabeledTextElementSize.Small]: {
        top: 12,
        bottom: 13,
    },
    [LabeledTextElementSize.Large]: {
        top: 13,
        bottom: 14,
    },
}

const fontColors: Record<ThemeVariant, { top: Color; bottom: Color }> = {
    [ThemeVariant.White]: {
        top: Color.TextDarkSecondary,
        bottom: Color.TextDarkPrimary,
    },
    [ThemeVariant.Black]: {
        top: Color.TextSecondary,
        bottom: Color.TextPrimary,
    },
}

export const LabeledTextElement: FCWithChildren<PropsWithChildren<LabeledTextElementProps>> = ({
    label,
    children,
    variant = ThemeVariant.White,
    size = LabeledTextElementSize.Large,
    reversed = false,
    spacing = 2,
    ...margins
}) => {
    const { top: fontTopColor, bottom: fontBottomColor } = fontColors[variant]
    const { top: fontTopSize, bottom: fontBottomSize } = fontSizes[size]

    const topColor = reversed ? fontBottomColor : fontTopColor
    const bottomColor = reversed ? fontTopColor : fontBottomColor
    const topSize = reversed ? fontBottomSize : fontTopSize
    const bottomSize = reversed ? fontTopSize : fontBottomSize
    return (
        <Box as='section' {...margins}>
            <Text
                wordBreak
                weight='regular'
                size={topSize}
                lineHeight='large'
                color={topColor}
                selectable
            >
                {label}
            </Text>
            {children && (
                <Text
                    paddingTop={spacing}
                    wordBreak
                    preLine
                    weight='regular'
                    lineHeight='large'
                    size={bottomSize}
                    color={bottomColor}
                    selectable
                >
                    {children}
                </Text>
            )}
        </Box>
    )
}
